import type { Paragraph, Table as DocxTable } from "docx";
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import { body, buildDocument, h1, factTable, table, fmtDate } from "@/lib/documentGenerator/docTheme";
import { documentFooter } from "@/lib/documentGenerator/documentFooter";
import { BASU_ROUTINE_SECTIONS, BASU_ROUTINE_STATUS_LABELS } from "@/lib/basProcess/basuRoutine";
import {
  BASU_CHECKLIST_SECTIONS,
  BASU_CHECKLIST_STATUS_LABELS,
} from "@/lib/basProcess/basuChecklist";
import { SAFETY_ACTIVITY_SECTIONS, SAFETY_ACTIVITY_STATUS_LABELS } from "@/lib/basProcess/safetyActivity";

const safe = (name?: string) => (name || "projekt").replace(/[^\w-]+/g, "_");

interface SectionDoc {
  name?: string;
  version?: string;
  status?: string;
  sections?: Record<string, string>;
}

/** Dok 40 – Rutin Bas-U */
export const generateBasuRoutineWordDocument = async (routine: SectionDoc, projectName?: string) => {
  const documentName = routine.name || "Rutin – Byggarbetsmiljösamordnare utförande (Bas-U)";
  const stamp = await createStampForCurrentUser({ docNumber: "40", documentName, projectName });
  const footer = await documentFooter({ docNumber: "40", documentType: "basu_rutin" });

  const children: (Paragraph | DocxTable)[] = BASU_ROUTINE_SECTIONS.flatMap((s, i) => [
    h1(`${i + 1}. ${s.title}`),
    body(routine.sections?.[s.key] ?? ""),
  ]);

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "40",
      documentName,
      projectName,
      version: routine.version || "1.0",
      status: BASU_ROUTINE_STATUS_LABELS[routine.status || ""] || routine.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok40_Rutin_BasU_${safe(projectName)}.docx`, {
    documentType: "basu_rutin",
    docNumber: "40",
    documentName,
    snapshot: routine,
    stamp,
  });
};

export interface BasuChecklistRow {
  section: string;
  activity: string;
  requirement?: string | null;
  method?: string | null;
  document_ref?: string | null;
  frequency?: string | null;
  responsible?: string | null;
  status: string;
  note?: string | null;
  due_date?: string | null;
}

/** Dok 41 – Checklista Bas-U */
export const generateBasuChecklistWordDocument = async (
  items: BasuChecklistRow[],
  projectName?: string
) => {
  const documentName = "Checklista Bas-U";
  const stamp = await createStampForCurrentUser({ docNumber: "41", documentName, projectName });
  const footer = await documentFooter({ docNumber: "41", documentType: "basu_checklista" });

  const children: (Paragraph | DocxTable)[] = BASU_CHECKLIST_SECTIONS.flatMap((section) => {
    const rows = items.filter((i) => i.section === section.key);
    if (!rows.length) return [];
    return [
      h1(section.label),
      table(
        ["Aktivitet", "Krav", "Kontroll", "Dokument", "Frekvens", "Ansvarig", "Status", "Kommentar"],
        rows.map((r) => [
          r.activity,
          r.requirement,
          r.method,
          r.document_ref,
          r.frequency,
          r.responsible,
          BASU_CHECKLIST_STATUS_LABELS[r.status] || r.status,
          [r.note, r.due_date ? `Senast ${fmtDate(r.due_date)}` : null].filter(Boolean).join(" · "),
        ])
      ),
    ];
  });

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "41", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok41_Checklista_BasU_${safe(projectName)}.docx`, {
    documentType: "basu_checklista",
    docNumber: "41",
    documentName,
    snapshot: items,
    stamp,
  });
};

/** Dok 42 – Plan för skyddsverksamhet */
export const generateSafetyActivityPlanWordDocument = async (
  plan: SectionDoc,
  projectName?: string
) => {
  const documentName = "Plan för skyddsverksamhet";
  const stamp = await createStampForCurrentUser({ docNumber: "42", documentName, projectName });
  const footer = await documentFooter({ docNumber: "42", documentType: "skyddsverksamhet" });

  const children: (Paragraph | DocxTable)[] = SAFETY_ACTIVITY_SECTIONS.flatMap((s, i) => [
    h1(`${i + 1}. ${s.title}`),
    body(plan.sections?.[s.key] ?? ""),
  ]);

  const doc = buildDocument({
    meta: {
      review: footer.review,
      docNumber: "42",
      documentName,
      projectName,
      version: plan.version || "1.0",
      status: SAFETY_ACTIVITY_STATUS_LABELS[plan.status || ""] || plan.status,
    },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok42_Skyddsverksamhet_${safe(projectName)}.docx`, {
    documentType: "skyddsverksamhet",
    docNumber: "42",
    documentName,
    snapshot: plan,
    stamp,
  });
};

export interface SitePresenceRow {
  handler_name?: string | null;
  handler_contact?: string | null;
  presence_level: string;
  presence_description?: string | null;
  agreed_with?: string | null;
  agreed_at?: string | null;
  contact_person_name?: string | null;
  contact_person_contact?: string | null;
  out_of_hours_routine?: string | null;
  escalation_routine?: string | null;
  followup_note?: string | null;
}

export const PRESENCE_LEVEL_LABELS: Record<string, string> = {
  heltid: "Heltid på arbetsplatsen",
  deltid: "Deltid enligt schema",
  regelbundna_besok: "Regelbundna besök",
  vid_behov: "Vid behov och vid riskfyllda moment",
};

/** Dok 43 – Platsnärvaro Bas-U */
export const generateSitePresenceWordDocument = async (
  plan: SitePresenceRow,
  projectName?: string
) => {
  const documentName = "Platsnärvaro Bas-U";
  const stamp = await createStampForCurrentUser({ docNumber: "43", documentName, projectName });
  const footer = await documentFooter({ docNumber: "43", documentType: "platsnarvaro" });

  const children: (Paragraph | DocxTable)[] = [
    h1("Överenskommen närvaro"),
    factTable([
      ["Handläggare Bas-U", plan.handler_name],
      ["Kontaktuppgifter", plan.handler_contact],
      ["Omfattning", PRESENCE_LEVEL_LABELS[plan.presence_level] || plan.presence_level],
      ["Beskrivning", plan.presence_description],
      ["Överenskommet med", plan.agreed_with],
      ["Datum", fmtDate(plan.agreed_at)],
    ]),
    h1("Kontaktperson på arbetsplatsen"),
    factTable([
      ["Namn", plan.contact_person_name],
      ["Kontaktuppgifter", plan.contact_person_contact],
    ]),
    h1("Rutiner"),
    factTable([
      ["Arbete utanför ordinarie tid", plan.out_of_hours_routine],
      ["Eskalering vid allvarlig risk", plan.escalation_routine],
      ["Uppföljning", plan.followup_note],
    ]),
  ];

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "43", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok43_Platsnarvaro_${safe(projectName)}.docx`, {
    documentType: "platsnarvaro",
    docNumber: "43",
    documentName,
    snapshot: plan,
    stamp,
  });
};

export interface TemporaryStructureRow {
  name: string;
  structure_type: string;
  category?: string | null;
  description?: string | null;
  supplier?: string | null;
  competent_person?: string | null;
  competence_verified: boolean;
  documentation_status: string;
  control_status: string;
  source_role?: string | null;
  note?: string | null;
}

export const STRUCTURE_TYPE_LABELS: Record<string, string> = {
  tillfallig: "Tillfällig konstruktion",
  samverkande: "Samverkande konstruktion",
};

export const STRUCTURE_DOC_LABELS: Record<string, string> = {
  saknas: "Saknas",
  begard: "Begärd",
  mottagen: "Mottagen",
  godkand: "Godkänd",
};

export const STRUCTURE_CONTROL_LABELS: Record<string, string> = {
  ej_kontrollerad: "Ej kontrollerad",
  kontrollerad: "Kontrollerad",
  brist: "Brist – åtgärd krävs",
};

/** Dok 44 – Tillfälliga och samverkande konstruktioner */
export const generateTemporaryStructuresWordDocument = async (
  rows: TemporaryStructureRow[],
  projectName?: string
) => {
  const documentName = "Tillfälliga och samverkande konstruktioner";
  const stamp = await createStampForCurrentUser({ docNumber: "44", documentName, projectName });
  const footer = await documentFooter({ docNumber: "44", documentType: "tillfalliga_konstruktioner" });

  const children: (Paragraph | DocxTable)[] = [
    h1("Register"),
    table(
      ["Konstruktion", "Typ", "Kategori", "Leverantör", "Kompetent person", "Handlingar", "Kontroll", "Kommentar"],
      rows.map((r) => [
        r.name,
        STRUCTURE_TYPE_LABELS[r.structure_type] || r.structure_type,
        r.category,
        r.supplier,
        `${r.competent_person || "–"}${r.competence_verified ? " (verifierad)" : ""}`,
        STRUCTURE_DOC_LABELS[r.documentation_status] || r.documentation_status,
        STRUCTURE_CONTROL_LABELS[r.control_status] || r.control_status,
        r.note,
      ])
    ),
  ];

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "44", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok44_Konstruktioner_${safe(projectName)}.docx`, {
    documentType: "tillfalliga_konstruktioner",
    docNumber: "44",
    documentName,
    snapshot: rows,
    stamp,
  });
};

export interface ToolboxTalkRow {
  topic: string;
  purpose?: string | null;
  held_at?: string | null;
  location?: string | null;
  language: string;
  leader?: string | null;
  companies?: string | null;
  activities?: string | null;
  incidents?: string | null;
  decisions?: string | null;
}

export interface ToolboxParticipantRow {
  person_name: string;
  company?: string | null;
  signed: boolean;
  note?: string | null;
}

/** Dok 45 – Toolbox talk */
export const generateToolboxTalkWordDocument = async (
  talk: ToolboxTalkRow,
  participants: ToolboxParticipantRow[],
  projectName?: string
) => {
  const documentName = `Toolbox talk – ${talk.topic}`;
  const stamp = await createStampForCurrentUser({ docNumber: "45", documentName, projectName });
  const footer = await documentFooter({ docNumber: "45", documentType: "toolbox_talk" });

  const children: (Paragraph | DocxTable)[] = [
    h1("Uppgifter"),
    factTable([
      ["Ämne", talk.topic],
      ["Syfte", talk.purpose],
      ["Datum", fmtDate(talk.held_at)],
      ["Plats", talk.location],
      ["Språk", talk.language === "en" ? "Engelska" : "Svenska"],
      ["Ledare", talk.leader],
      ["Företag på plats", talk.companies],
    ]),
    h1("Genomgång"),
    body(talk.activities ?? ""),
    h1("Tillbud och observationer"),
    body(talk.incidents ?? ""),
    h1("Beslut och åtgärder"),
    body(talk.decisions ?? ""),
    h1("Deltagare"),
    table(
      ["Namn", "Företag", "Signerat", "Kommentar"],
      participants.map((p) => [p.person_name, p.company, p.signed ? "Ja" : "Nej", p.note])
    ),
  ];

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "45", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok45_ToolboxTalk_${safe(projectName)}.docx`, {
    documentType: "toolbox_talk",
    docNumber: "45",
    documentName,
    snapshot: { talk, participants },
    stamp,
  });
};

export interface DisciplinaryRow {
  occurred_at?: string | null;
  company?: string | null;
  person_name?: string | null;
  rule_broken?: string | null;
  description?: string | null;
  action_level: string;
  action_taken?: string | null;
  decided_by?: string | null;
  followup?: string | null;
  status: string;
}

export const ACTION_LEVEL_LABELS: Record<string, string> = {
  tillsagelse: "Muntlig tillsägelse",
  skriftlig_varning: "Skriftlig varning",
  stoppat_arbete: "Stoppat arbete",
  avvisning: "Avvisning från arbetsplatsen",
};

export const ACTION_STATUS_LABELS: Record<string, string> = {
  oppen: "Öppen",
  atgardad: "Åtgärdad",
  eskalerad: "Eskalerad",
};

/** Dok 46 – Disciplinära åtgärder */
export const generateDisciplinaryActionsWordDocument = async (
  rows: DisciplinaryRow[],
  projectName?: string
) => {
  const documentName = "Disciplinära åtgärder";
  const stamp = await createStampForCurrentUser({ docNumber: "46", documentName, projectName });
  const footer = await documentFooter({ docNumber: "46", documentType: "disciplinara_atgarder" });

  const children: (Paragraph | DocxTable)[] = [
    h1("Logg"),
    table(
      ["Datum", "Företag", "Person", "Regel", "Händelse", "Åtgärd", "Beslutad av", "Uppföljning", "Status"],
      rows.map((r) => [
        fmtDate(r.occurred_at),
        r.company,
        r.person_name,
        r.rule_broken,
        r.description,
        `${ACTION_LEVEL_LABELS[r.action_level] || r.action_level}${r.action_taken ? ` – ${r.action_taken}` : ""}`,
        r.decided_by,
        r.followup,
        ACTION_STATUS_LABELS[r.status] || r.status,
      ])
    ),
  ];

  const doc = buildDocument({
    meta: { review: footer.review, docNumber: "46", documentName, projectName },
    stamp,
    children: [...children, ...footer.children],
  });

  await downloadWordDocument(doc, `Dok46_Disciplinara_atgarder_${safe(projectName)}.docx`, {
    documentType: "disciplinara_atgarder",
    docNumber: "46",
    documentName,
    snapshot: rows,
    stamp,
  });
};
